// src/modules/operaciones/inventarios/utils/detectorColumnas.js
//
// Interpreta las columnas que entrega el parser del backend y les asigna un ROL.
//
// Entrada: el array `parseado.columnas` del parser. Cada columna trae:
//   { id: "col5", indice: 5, nombre: "MERMA DESCONOCIDA › GRAN COLOMBIA › $",
//     encabezado: ["MERMA DESCONOCIDA", "GRAN COLOMBIA", "$"], tipo, muestras, valoresUnicos }
//
// Salida de detectarRoles(columnas): ver el bloque "Resultado" más abajo.
// Todas las columnas se devuelven como IDS ("col5"), que son las claves que usan las filas:
//   fila["col5"].
//
// Este módulo NO calcula nada con los datos (eso es trabajo de analizarMermas.js y
// analizarDatos.js). Solo contesta: "¿qué significa cada columna?".
//
// Exporta:
//   normalizarNumero, aNumero      → texto/valor → número ("-$63.663,84" → -63663.84)
//   normalizarTexto, claveTexto    → limpieza de texto (espacios, tildes, mayúsculas)
//   derivarFamilia                 → primera palabra de la descripción (CONCENT, SNACK...)
//   esFilaTotal, separarFilasTotal → detección de la fila "Total general"
//   detectarRoles                  → el detector principal (también es el export por defecto)

/* ========================================================================== */
/* 1. Texto                                                                   */
/* ========================================================================== */

// Limpia un texto: espacios invisibles, espacios repetidos y tildes.
// Nota: al quitar tildes la "ñ" pasa a "n" (PEQUEÑOS → PEQUENOS), lo que ayuda a comparar.
//   normalizarTexto("  Gran   Colombia ")                       → "Gran Colombia"
//   normalizarTexto("Ñandú", { mayusculas: true })              → "NANDU"
//   normalizarTexto("Medellín", { conservarTildes: true })      → "Medellín"
export function normalizarTexto(valor, { mayusculas = false, minusculas = false, conservarTildes = false } = {}) {
  if (valor === null || valor === undefined) return "";
  let s = String(valor).replace(/[\u00A0\u200B]/g, " ");
  if (!conservarTildes) s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  s = s.replace(/\s+/g, " ").trim();
  if (mayusculas) s = s.toUpperCase();
  else if (minusculas) s = s.toLowerCase();
  return s;
}

// Clave de comparación: minúsculas, sin tildes y sin signos (conserva letras, números, $ y %).
//   claveTexto("MERMA CONOCIDA-DESCONOCIDA") → "merma conocida desconocida"
//   claveTexto(" $ ")                         → "$"
export function claveTexto(valor) {
  return normalizarTexto(valor, { minusculas: true })
    .replace(/[^a-z0-9$%\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// "GRAN COLOMBIA" → "Gran Colombia"; conserva siglas conocidas ("BODEGA CEDI" → "Bodega CEDI").
const SIGLAS = new Set(["CEDI", "SA", "SAS", "LTDA", "CC", "UND", "KG", "PRO", "II", "III", "IV"]);
const CONECTORES = new Set(["de", "del", "la", "el", "y", "e", "en"]);

export function tituloTexto(valor) {
  const palabras = normalizarTexto(valor, { conservarTildes: true }).split(" ").filter(Boolean);
  return palabras
    .map((p, i) => {
      if (SIGLAS.has(p.toUpperCase())) return p.toUpperCase();
      const min = p.toLowerCase();
      if (i > 0 && CONECTORES.has(min)) return min;
      return min.charAt(0).toUpperCase() + min.slice(1);
    })
    .join(" ");
}

/* ========================================================================== */
/* 2. Números                                                                 */
/* ========================================================================== */

// Guion contable: "-", " - ", "$-", " $-   " → se interpreta como 0.
const RE_GUION = /^\$?\s*-\s*\$?$/;

// Convierte cualquier representación de número en un número de JavaScript.
// Es la MISMA lógica del parser del backend, para que ambos lados interpreten igual.
//
//   normalizarNumero("-$63.663,84")      → -63663.84     (formato colombiano)
//   normalizarNumero("-$63,663.84 ")     → -63663.84     (formato US)
//   normalizarNumero(" $12,484,705.60 ") → 12484705.6
//   normalizarNumero("(1.234,56)")       → -1234.56      (negativo contable)
//   normalizarNumero("123-")             → -123
//   normalizarNumero(" $-   ")           → 0             (guion contable)
//   normalizarNumero(4185.6)             → 4185.6
//   normalizarNumero("abc")              → null          (no interpretable)
//   normalizarNumero("")                 → null
//
// Reglas de separadores:
//  - Con coma y punto: el que aparece de último es el decimal.
//  - Solo comas: "1,234" / "1,234,567" = miles; "1234,56" / "1,5" = decimal.
//  - Solo puntos: "1.234.567" = miles; un único punto ("1.5", "1.234") = decimal.
export function normalizarNumero(valor) {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor === "boolean" || valor instanceof Date) return null;

  let s = String(valor).replace(/\u00A0/g, " ").replace(/[−–—]/g, "-").trim();
  if (s === "") return null;
  if (RE_GUION.test(s)) return 0;

  let negativo = false;
  const parentesis = s.match(/^\((.*)\)$/);
  if (parentesis) {
    negativo = true;
    s = parentesis[1].trim();
  }

  s = s.replace(/COP|USD|EUR/gi, "").replace(/[$€£%\s]/g, "");
  if (s === "") return null;

  if (/^[\d.,]+-$/.test(s)) {
    negativo = !negativo;
    s = s.slice(0, -1);
  }
  if (s.startsWith("+")) s = s.slice(1);
  if (s.startsWith("-")) {
    negativo = !negativo;
    s = s.slice(1);
  }
  if (!/^[\d.,]+$/.test(s)) return null;

  const tieneComa = s.includes(",");
  const tienePunto = s.includes(".");
  if (tieneComa && tienePunto) {
    const decimal = s.lastIndexOf(",") > s.lastIndexOf(".") ? "," : ".";
    const miles = decimal === "," ? /\./g : /,/g;
    s = s.replace(miles, "").replace(decimal, ".");
  } else if (tieneComa) {
    s = /^\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, "") : s.replace(",", ".");
  } else if (tienePunto && (s.match(/\./g) || []).length > 1) {
    if (!/^\d{1,3}(\.\d{3})+$/.test(s)) return null;
    s = s.replace(/\./g, "");
  }

  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  return negativo ? -n : n;
}

// Igual que normalizarNumero pero devuelve `defecto` (0) cuando no hay número. Ideal para sumar.
export function aNumero(valor, defecto = 0) {
  const n = normalizarNumero(valor);
  return n === null ? defecto : n;
}

/* ========================================================================== */
/* 3. Familia derivada de la descripción                                      */
/* ========================================================================== */

// Abreviaturas frecuentes → nombre completo. Se puede ampliar sin tocar la lógica.
export const ALIAS_FAMILIA = {
  CONCENT: "CONCENTRADO",
  MED: "MEDICAMENTOS",
  GALLET: "GALLETAS",
  HUESO: "HUESOS",
  TAPETE: "TAPETES",
  TRABILLA: "TRAILLA",
};

export const FAMILIA_VACIA = "SIN FAMILIA";

// Primera palabra "con sentido" (2 o más letras) de la descripción, en mayúsculas y sin tildes.
//   derivarFamilia("CONCENT FILPO x KILO AGRANEL ADULTO") → "CONCENTRADO"
//   derivarFamilia("SNACK CHURU 4x14g TUNA")               → "SNACK"
//   derivarFamilia("COMEDERO/BEBEDERO DOBLE")              → "COMEDERO"
//   derivarFamilia("H CAMA GRANDE")                        → "CAMA"   (salta letras sueltas)
//   derivarFamilia("")                                     → "SIN FAMILIA"
export function derivarFamilia(descripcion, alias = ALIAS_FAMILIA) {
  const texto = normalizarTexto(descripcion, { mayusculas: true });
  if (!texto) return FAMILIA_VACIA;
  const palabras = texto.split(/[^A-Z0-9&]+/).filter(Boolean);
  const palabra = palabras.find((p) => (p.match(/[A-Z]/g) || []).length >= 2);
  if (!palabra) return FAMILIA_VACIA;
  return alias[palabra] || palabra;
}

/* ========================================================================== */
/* 4. Fila "Total general"                                                    */
/* ========================================================================== */

// "Total general", "Total", "Totales", "Subtotal", "Gran total"
const RE_FILA_TOTAL = /^\s*(gran\s+)?(sub)?total(es)?(\s+general)?\s*:?\s*$/i;
const COLUMNAS_ETIQUETA = 3; // la etiqueta del total siempre está en las primeras columnas

const vacio = (v) => v === null || v === undefined || String(v).trim() === "";

// Primera celda no vacía entre las columnas de etiqueta de la fila.
function etiquetaDeFila(fila, roles) {
  const ids = roles?.filasTotal?.columnas || [];
  for (const id of ids) {
    if (!vacio(fila?.[id])) return fila[id];
  }
  return null;
}

// ¿Esta fila es un total ("Total general", "Subtotal"...)?
export function esFilaTotal(fila, roles) {
  const etiqueta = etiquetaDeFila(fila, roles);
  return typeof etiqueta === "string" && RE_FILA_TOTAL.test(etiqueta);
}

// Separa las filas de datos de las filas de totales.
// - Si el parser ya entregó `filaTotal` se usa esa; si no (informes guardados antes del cambio
//   del parser), se busca dentro de `filas`.
// - Devuelve siempre `filas` SIN totales, para no duplicar las sumas.
//   const { filas, filaTotal } = separarFilasTotal(parseado.filas, roles, parseado.filaTotal);
export function separarFilasTotal(filas, roles, filaTotalParser = null) {
  const datos = [];
  const totales = [];
  for (const f of filas || []) (esFilaTotal(f, roles) ? totales : datos).push(f);
  const filaTotal =
    filaTotalParser ||
    totales.find((t) => /general/i.test(String(etiquetaDeFila(t, roles)))) ||
    totales[totales.length - 1] ||
    null;
  return { filas: datos, filaTotal, otrasFilasTotales: totales.filter((t) => t !== filaTotal) };
}

/* ========================================================================== */
/* 5. Análisis de encabezados tipo "MERMA DESCONOCIDA › GRAN COLOMBIA › $"    */
/* ========================================================================== */

const RE_MERMA = /\bmermas?\b/;
const RE_TOTAL = /\btotal(es)?\b/;
const RE_VENTA = /\bventas?\b/;
const RE_DESCONOCIDA = /\bdesconocid[ao]s?\b/;
const RE_CONOCIDA = /\bconocid[ao]s?\b/;
const RE_ENTRADA = /\bentradas?\b/;
const RE_SALIDA = /\b(salidas?|salidad)\b/; // "SALIDAD" es un typo frecuente en los archivos
const RE_MEDIDA_CANTIDAD = /^(cantidad(es)?|cant|unidades|und|unds|qty)$/;
const RE_MEDIDA_VALOR = /^(\$|valor(es)?|vlr|importe|monto|pesos|costo)$/;

// Partes del encabezado. Si el parser es antiguo y no trae `encabezado`, se separa el nombre.
function partesDeColumna(col) {
  if (Array.isArray(col.encabezado) && col.encabezado.length > 0) {
    return col.encabezado.map((p) => normalizarTexto(p, { conservarTildes: true })).filter(Boolean);
  }
  return String(col.nombre || "")
    .split("›")
    .map((p) => normalizarTexto(p, { conservarTildes: true }))
    .filter(Boolean);
}

// Quita la palabra de movimiento de un nivel ("SAN JORGE ENTRADA" → "SAN JORGE").
const quitarMovimiento = (texto) =>
  normalizarTexto(texto.replace(/\b(entradas?|salidas?|salidad)\b/gi, " "), { conservarTildes: true });

// Devuelve qué significa UNA columna:
//   grupo: "merma" | "total_merma" | "venta" | null
//   tipoMerma: "desconocida" | "conocida" | "ambas" | "general" | null
//   bodega: texto | null     movimiento: "entrada" | "salida" | null
//   medida: "cantidad" | "valor" | null
function analizarEncabezado(col) {
  const vacioInfo = { grupo: null, tipoMerma: null, bodega: null, movimiento: null, medida: null };
  const partes = partesDeColumna(col);
  if (partes.length < 2) return vacioInfo;

  const claves = partes.map(claveTexto);
  const hoja = claves[claves.length - 1];
  const medida = RE_MEDIDA_CANTIDAD.test(hoja) ? "cantidad" : RE_MEDIDA_VALOR.test(hoja) ? "valor" : null;
  if (!medida) return vacioInfo;

  const niveles = partes.slice(0, -1);
  const clavesNiveles = claves.slice(0, -1);

  const hayMerma = clavesNiveles.some((k) => RE_MERMA.test(k));
  const hayVenta = clavesNiveles.some((k) => RE_VENTA.test(k));
  const esTotal = clavesNiveles.some((k) => RE_TOTAL.test(k));

  let grupo = null;
  if (hayMerma) grupo = esTotal ? "total_merma" : "merma";
  else if (hayVenta) grupo = "venta";
  if (!grupo) return vacioInfo;

  // Tipo de merma. OJO: "desconocida" contiene "conocida"; \b evita confundirlas.
  const tieneDes = clavesNiveles.some((k) => RE_DESCONOCIDA.test(k));
  const tieneCon = clavesNiveles.some((k) => RE_CONOCIDA.test(k));
  let tipoMerma = null;
  if (grupo !== "venta") {
    tipoMerma = tieneDes && tieneCon ? "ambas" : tieneDes ? "desconocida" : tieneCon ? "conocida" : "general";
  }

  // Movimiento y bodega: lo que quede en los niveles después de quitar etiquetas
  // ("MERMA ...", "TOTAL ...", "VENTA") y la palabra ENTRADA/SALIDA.
  let movimiento = null;
  const restos = [];
  niveles.forEach((nivel, i) => {
    const k = clavesNiveles[i];
    let mov = null;
    if (RE_ENTRADA.test(k)) mov = "entrada";
    else if (RE_SALIDA.test(k)) mov = "salida";
    if (mov && !movimiento) movimiento = mov;

    const esEtiqueta = RE_MERMA.test(k) || RE_TOTAL.test(k) || RE_VENTA.test(k);
    if (esEtiqueta) return;
    const resto = mov ? quitarMovimiento(nivel) : nivel;
    if (resto) restos.push(resto);
  });

  const bodega = grupo === "merma" && restos.length > 0 ? restos[0] : null;
  return { grupo, tipoMerma, bodega, movimiento, medida };
}

const claveBodega = (b) => (b ? normalizarTexto(b, { mayusculas: true }) : "");

// ¿Dos columnas contiguas forman un par cantidad + valor del mismo bloque?
function formanPar(a, b) {
  return (
    a.medida && b.medida && a.medida !== b.medida &&
    a.grupo === b.grupo &&
    a.tipoMerma === b.tipoMerma &&
    (!a.bodega || !b.bodega || claveBodega(a.bodega) === claveBodega(b.bodega)) &&
    (!a.movimiento || !b.movimiento || a.movimiento === b.movimiento)
  );
}

/* ========================================================================== */
/* 6. Roles genéricos (modo de respaldo para otros archivos)                  */
/* ========================================================================== */

// Cada regla: tipos de columna admitidos y patrones (sobre claveTexto) con su peso.
// Un patrón que coincide con el ÚLTIMO nivel del encabezado vale su peso completo;
// si coincide con otro nivel vale la mitad.
const REGLAS = {
  item: {
    tipos: ["codigo", "texto", "numero"],
    patrones: [
      { re: /^(item|items|codigo|cod|sku|ean|upc|referencia|ref|id)\b/, peso: 3 },
      { re: /\b(codigo|cod)\b/, peso: 2 },
    ],
    bonusTipo: { codigo: 2 },
  },
  descripcion: {
    tipos: ["texto"],
    patrones: [{ re: /\b(descripcion|articulo|producto|nombre|detalle)\b/, peso: 3 }],
  },
  unidad: {
    tipos: ["texto"],
    patrones: [
      { re: /^(und|unidad(es)?|u m|um)(\s+(de\s+)?medida)?$/, peso: 3 },
      { re: /\bmedida\b/, peso: 2 },
    ],
  },
  fabricante: {
    tipos: ["texto"],
    patrones: [{ re: /\b(fabricante|laboratorio|productor)\b/, peso: 3 }],
  },
  marca: { tipos: ["texto"], patrones: [{ re: /\b(marca|brand)\b/, peso: 3 }] },
  proveedor: {
    tipos: ["texto"],
    patrones: [{ re: /\b(proveedor(es)?|supplier|vendor|distribuidor)\b/, peso: 3 }],
  },
  bodega: {
    tipos: ["texto"],
    patrones: [{ re: /\b(bodega|almacen|sede|sucursal|ubicacion|deposito|tienda)\b/, peso: 3 }],
  },
  categoria: {
    tipos: ["texto"],
    patrones: [{ re: /\b(categoria|familia|linea|subgrupo|grupo|clase|tipo)\b/, peso: 2.5 }],
  },
  fecha: {
    tipos: ["fecha", "texto"],
    patrones: [{ re: /\b(fecha|date|periodo|mes)\b/, peso: 3 }],
    bonusTipo: { fecha: 3 },
  },
  cantidad: {
    tipos: ["numero", "moneda"],
    patrones: [{ re: /\b(cantidad(es)?|cant|stock|existencias?|unidades|saldo|inventario)\b/, peso: 3 }],
  },
  costo: { tipos: ["numero", "moneda"], patrones: [{ re: /\b(costos?|cost)\b/, peso: 3 }] },
  precio: { tipos: ["numero", "moneda"], patrones: [{ re: /\b(precio|pvp|price|tarifa)\b/, peso: 3 }] },
  valor: {
    tipos: ["numero", "moneda"],
    patrones: [
      { re: /\b(valor|importe|monto|total|ventas?|ingresos?)\b/, peso: 2.5 },
      { re: /^\$$/, peso: 2 },
    ],
  },
  ganancia: {
    tipos: ["numero", "moneda"],
    patrones: [{ re: /\b(ganancia|utilidad|margen|profit|rentabilidad|beneficio)\b/, peso: 3 }],
  },
  perdida: {
    tipos: ["numero", "moneda"],
    patrones: [{ re: /\b(perdidas?|merma|faltantes?|diferencia|sobrantes?|ajuste)\b/, peso: 3 }],
  },
};

// Orden de prioridad para desempates.
const ORDEN_ROLES = Object.keys(REGLAS);

const ROLES_SUPERIORES = ["itemCol", "descripcionCol", "unidadCol", "fabricanteCol"];
const ROLES_GENERICOS = [
  "marca", "proveedor", "bodega", "categoria", "cantidad", "costo", "precio",
  "valor", "ganancia", "perdida", "fecha",
];

function puntuar(col, regla) {
  if (!regla.tipos.includes(col.tipo)) return 0;
  const partes = partesDeColumna(col).map(claveTexto);
  if (partes.length === 0) return regla.bonusTipo?.[col.tipo] ? regla.bonusTipo[col.tipo] : 0;
  const hoja = partes[partes.length - 1];
  const otros = partes.slice(0, -1);
  let score = 0;
  for (const { re, peso } of regla.patrones) {
    if (re.test(hoja)) score += peso;
    else if (otros.some((o) => re.test(o))) score += peso * 0.5;
  }
  // El bonus por tipo solo suma si ya hay alguna coincidencia por nombre,
  // salvo en "fecha", donde una columna de fechas vale por sí sola.
  if (regla.bonusTipo?.[col.tipo] && (score > 0 || col.tipo === "fecha")) score += regla.bonusTipo[col.tipo];
  return score;
}

// Asigna cada rol a UNA columna distinta (la de mayor puntaje). Ignora `excluidas`.
function asignarRoles(columnas, excluidas) {
  const candidatos = [];
  for (const col of columnas) {
    if (excluidas.has(col.id)) continue;
    ORDEN_ROLES.forEach((rol, prioridad) => {
      const score = puntuar(col, REGLAS[rol]);
      if (score > 0) candidatos.push({ rol, col, score, prioridad });
    });
  }
  candidatos.sort(
    (a, b) => b.score - a.score || (a.col.indice ?? 0) - (b.col.indice ?? 0) || a.prioridad - b.prioridad
  );
  const porRol = {};
  const usadas = new Set();
  for (const c of candidatos) {
    if (porRol[c.rol] || usadas.has(c.col.id)) continue;
    porRol[c.rol] = c.col.id;
    usadas.add(c.col.id);
  }
  return porRol;
}

/* ========================================================================== */
/* 7. Detector principal                                                      */
/* ========================================================================== */

// detectarRoles(columnas, { mapeoManual })
//
// RESULTADO
// {
//   modo: "mermas" | "generico",          // "mermas" si se detectó una matriz de mermas por bodega
//   esMatrizMermas: boolean,
//
//   // Columnas fijas (ids o null)
//   itemCol, descripcionCol, unidadCol, fabricanteCol,
//
//   // Un bloque por cada par (cantidad, valor) de merma
//   bloques: [{
//     id: "conocida|SAN JORGE|entrada",
//     tipo: "desconocida" | "conocida" | "ambas" | "general",   // "ambas" = resumen: NO sumar con los demás
//     bodega: "SAN JORGE" | null,  bodegaEtiqueta: "San Jorge",
//     movimiento: "entrada" | "salida" | null,
//     columnaCantidad: "col18" | null,  columnaValor: "col19" | null,
//     etiqueta: "San Jorge · Conocida (entrada)",
//   }],
//   bodegas: [{ clave: "SAN JORGE", etiqueta: "San Jorge" }],   // en orden de aparición
//
//   // Columnas de total (sirven para VALIDAR el cálculo, no para volver a sumar)
//   totalesMerma: { desconocida, conocida, total },   // cada una { columnaCantidad, columnaValor } | null
//   venta: { columnaCantidad, columnaValor } | null,
//
//   filasTotal: { columnas: ["col0","col1","col2"], patron: "..." },   // ver esFilaTotal()
//   familiaDerivada: { columnaFuente: "col1" | null, disponible: boolean },
//
//   // Roles para el modo genérico (otros Excel, CSV, PDF, Word)
//   genericos: { marca, proveedor, bodega, categoria, cantidad, costo, precio, valor, ganancia, perdida, fecha },
//
//   columnasUsadas: ["col4", ...],   // las consumidas por bloques, totales y venta
//   advertencias: string[],
// }
//
// mapeoManual (opcional): { itemCol: "col0", fabricanteCol: null, marca: "col7", ... }
//   Un id válido reemplaza la detección; null o "" deja el rol vacío.
export function detectarRoles(columnas, { mapeoManual = null } = {}) {
  const cols = Array.isArray(columnas) ? columnas : [];
  const advertencias = [];
  const pos = (c, i) => (Number.isFinite(c.indice) ? c.indice : i);

  // 1) Qué significa el encabezado de cada columna
  const info = cols.map((col, i) => ({ col, pos: pos(col, i), ...analizarEncabezado(col) }));

  // 2) Une columnas contiguas en pares cantidad + valor
  const unidades = []; // cada una: { cantidad, valor, ...datos fusionados }
  const consumida = new Set();
  for (let i = 0; i < info.length; i++) {
    const a = info[i];
    if (!a.grupo || consumida.has(a.col.id)) continue;
    const b = info[i + 1];
    if (b && b.grupo && !consumida.has(b.col.id) && b.pos === a.pos + 1 && formanPar(a, b)) {
      const cantidad = a.medida === "cantidad" ? a : b;
      const valor = a.medida === "valor" ? a : b;
      consumida.add(a.col.id);
      consumida.add(b.col.id);
      unidades.push({
        grupo: a.grupo,
        tipoMerma: a.tipoMerma,
        bodega: a.bodega || b.bodega || null,
        movimiento: a.movimiento || b.movimiento || null,
        esTotal: /total/.test(claveTexto((a.col.encabezado || [])[0] || a.col.nombre)),
        columnaCantidad: cantidad.col.id,
        columnaValor: valor.col.id,
      });
    } else {
      consumida.add(a.col.id);
      unidades.push({
        grupo: a.grupo,
        tipoMerma: a.tipoMerma,
        bodega: a.bodega,
        movimiento: a.movimiento,
        esTotal: /total/.test(claveTexto((a.col.encabezado || [])[0] || a.col.nombre)),
        columnaCantidad: a.medida === "cantidad" ? a.col.id : null,
        columnaValor: a.medida === "valor" ? a.col.id : null,
      });
    }
  }

  // 3) Reparte las unidades: bloques de merma, totales de merma y venta
  const bloques = [];
  const idsVistos = new Map();
  const totalesMerma = { desconocida: null, conocida: null, total: null };
  const candidatosVenta = [];

  for (const u of unidades) {
    const par = { columnaCantidad: u.columnaCantidad, columnaValor: u.columnaValor };
    if (u.grupo === "merma") {
      const base = `${u.tipoMerma}|${claveBodega(u.bodega) || "GENERAL"}|${u.movimiento || "-"}`;
      const n = (idsVistos.get(base) || 0) + 1;
      idsVistos.set(base, n);
      const bodegaEtiqueta = u.bodega ? tituloTexto(u.bodega) : "General";
      const tipoTxt = u.tipoMerma.charAt(0).toUpperCase() + u.tipoMerma.slice(1);
      bloques.push({
        id: n > 1 ? `${base}#${n}` : base,
        tipo: u.tipoMerma,
        bodega: u.bodega ? claveBodega(u.bodega) : null,
        bodegaEtiqueta,
        movimiento: u.movimiento,
        ...par,
        etiqueta: `${bodegaEtiqueta} · ${tipoTxt}${u.movimiento ? ` (${u.movimiento})` : ""}`,
      });
      if (!par.columnaCantidad || !par.columnaValor) {
        advertencias.push(
          `El bloque "${bodegaEtiqueta} · ${tipoTxt}" no tiene ${par.columnaCantidad ? "columna de valor ($)" : "columna de cantidad"}.`
        );
      }
    } else if (u.grupo === "total_merma") {
      const clave = u.tipoMerma === "ambas" || u.tipoMerma === "general" ? "total" : u.tipoMerma;
      if (!totalesMerma[clave]) totalesMerma[clave] = par;
    } else if (u.grupo === "venta") {
      candidatosVenta.push({ ...par, esTotal: u.esTotal });
    }
  }
  const ventaElegida = candidatosVenta.find((v) => v.esTotal) || candidatosVenta[0] || null;
  const venta = ventaElegida
    ? { columnaCantidad: ventaElegida.columnaCantidad, columnaValor: ventaElegida.columnaValor }
    : null;

  // 4) ¿Es una matriz de mermas? Al menos 2 bloques de merma conocida/desconocida con valor ($)
  const bloquesUtiles = bloques.filter(
    (b) => (b.tipo === "conocida" || b.tipo === "desconocida") && b.columnaValor
  );
  const esMatrizMermas = bloquesUtiles.length >= 2;
  const modo = esMatrizMermas ? "mermas" : "generico";

  // Columnas que ya tienen dueño (solo cuenta en modo mermas)
  const usadas = new Set();
  if (esMatrizMermas) {
    bloques.forEach((b) => [b.columnaCantidad, b.columnaValor].forEach((c) => c && usadas.add(c)));
    Object.values(totalesMerma).forEach((t) => t && [t.columnaCantidad, t.columnaValor].forEach((c) => c && usadas.add(c)));
    if (venta) [venta.columnaCantidad, venta.columnaValor].forEach((c) => c && usadas.add(c));
  }

  // 5) Roles fijos y genéricos por nombre de columna
  const asignados = asignarRoles(cols, usadas);
  const roles = {
    itemCol: asignados.item || null,
    descripcionCol: asignados.descripcion || null,
    unidadCol: asignados.unidad || null,
    fabricanteCol: asignados.fabricante || asignados.marca || asignados.proveedor || null,
  };
  const genericos = {};
  ROLES_GENERICOS.forEach((r) => {
    genericos[r] = asignados[r] || null;
  });

  // 6) Mapeo manual (corrección del usuario)
  if (mapeoManual && typeof mapeoManual === "object") {
    const existentes = new Set(cols.map((c) => c.id));
    const aplicar = (clave, destino) => {
      if (!Object.prototype.hasOwnProperty.call(mapeoManual, clave)) return;
      const v = mapeoManual[clave];
      if (v === null || v === "") destino[clave] = null;
      else if (existentes.has(v)) destino[clave] = v;
    };
    ROLES_SUPERIORES.forEach((r) => aplicar(r, roles));
    ROLES_GENERICOS.forEach((r) => aplicar(r, genericos));
    advertencias.push("Se aplicó una asignación manual de columnas.");
  }

  // 7) Avisos útiles para el usuario
  if (esMatrizMermas) {
    if (!roles.itemCol) advertencias.push("No se encontró la columna de código (ITEM).");
    if (!roles.descripcionCol) advertencias.push("No se encontró la columna de descripción.");
    if (!totalesMerma.total && !totalesMerma.conocida && !totalesMerma.desconocida) {
      advertencias.push("No se encontraron columnas de TOTAL MERMA: no se podrá validar el cálculo.");
    }
    if (!venta) advertencias.push("No se encontró la columna de venta.");
    if (bloques.some((b) => b.tipo === "ambas")) {
      advertencias.push('Hay bloques "conocida-desconocida" por bodega: se tratan como resumen y no se suman con los demás.');
    }
  }

  // 8) Bodegas en orden de aparición
  const bodegas = [];
  const bodegasVistas = new Set();
  bloques.forEach((b) => {
    if (b.bodega && !bodegasVistas.has(b.bodega)) {
      bodegasVistas.add(b.bodega);
      bodegas.push({ clave: b.bodega, etiqueta: b.bodegaEtiqueta });
    }
  });

  // 9) Fila de totales y familia derivada
  const primeras = [...cols]
    .sort((a, b) => (a.indice ?? 0) - (b.indice ?? 0))
    .slice(0, COLUMNAS_ETIQUETA)
    .map((c) => c.id);

  return {
    modo,
    esMatrizMermas,
    ...roles,
    bloques,
    bodegas,
    totalesMerma,
    venta,
    filasTotal: { columnas: primeras, patron: RE_FILA_TOTAL.source },
    familiaDerivada: { columnaFuente: roles.descripcionCol, disponible: Boolean(roles.descripcionCol) },
    genericos,
    columnasUsadas: Array.from(usadas),
    advertencias,
  };
}

export default detectarRoles;